import { Component, Input, computed, signal } from '@angular/core';
import { ImageWidget, Theme, WidgetColors } from '../../models/reader-document';
import { DEFAULT_COLORS, DEFAULT_PADDING } from '../../utils/widget.constants';
import { resolveWidgetStyleColors } from '../../utils/widget-style.utils';

@Component({
  selector: 'wr-image-widget',
  standalone: true,
  template: `
    <div
      class="wr-image-widget px-4"
      [style.background]="background()"
      [style.padding-top.px]="paddingTop()"
      [style.padding-bottom.px]="paddingBottom()"
      [style.text-align]="textAlign()"
    >
      <!-- Title / Subtitle -->
      <div class="px-1">
        @if (widget()?.title || widget()?.subtitle){
        <div class="mb-2">
          @if (widget()?.title){
          <div
            [style.color]="titleColor()"
            [style.font-family]="titleFont()"
            class="title"
          >
            {{ widget()?.title }}
          </div>
          }
          @if (widget()?.subtitle){
            <div
              [style.color]="subtitleColor()"
              [style.font-family]="subtitleFont()"
              class="subtitle"
            >
              {{ widget()?.subtitle }}
            </div>
          }
        </div>
        }
      </div>

      <!-- Image -->
      <!-- TODO TAILWIND TO CSS? -->
      @if (imageSrc(); as src) {
        @if (linkUrl()) {
          <a
            [href]="linkUrl()"
            target="_blank"
            class="hover:opacity-80 duration-400 ease-in-out"
            rel="noopener noreferrer"
          >
            <img
              class="block w-full"
              [src]="src"
              [alt]="altText()"
              [style.border-radius.px]="cornerRadius()"
              (error)="onImgError($event)"
            />
          </a>
        } @else {
          <img
            class="block w-full"
            [src]="src"
            [alt]="altText()"
            [style.border-radius.px]="cornerRadius()"
            (error)="onImgError($event)"
          />
        }
      }
    </div>
  `,
})
export class ImageWidgetComponent {
  private widgetSig = signal<ImageWidget | null>(null);
  private themeSig = signal<Theme & { isDark?: boolean } | null>(null);
  private imgSrcSig = signal<string | null>(null);

  @Input() set json(value: ImageWidget) {
    this.widgetSig.set(value);
    if (value) {
      this.imgSrcSig.set(value.url || value.fallbackUrl || null);
    } else {
      this.imgSrcSig.set(null);
    }
  }

  @Input() set theme(value: Theme | undefined) {
    this.themeSig.set(value ?? null);
  }

  // basics

  widget = computed(() => this.widgetSig());
  imageSrc = computed(() => this.imgSrcSig());
  linkUrl = computed(() => this.widget()?.linkURL || null);
  altText = computed(
    () =>
      this.widget()?.altTitle ||
      this.widget()?.title ||
      this.widget()?.name ||
      this.widget()?.id ||
      ''
  );

  mergedColors = computed<WidgetColors>(() => {
    const w = this.widget();
    const theme = this.themeSig();
    const isDark = theme?.isDark ?? false;

    const themeColors: WidgetColors = theme
      ? { ...theme[isDark ? 'darkColors' : 'lightColors'] }
      : {};

    const widgetColors: WidgetColors = resolveWidgetStyleColors(w?.widgetStyle, isDark);

    return {
      accent: widgetColors.accent ?? themeColors.accent ?? DEFAULT_COLORS.accent,
      title: widgetColors.title ?? themeColors.title ?? DEFAULT_COLORS.title,
      subtitle: widgetColors.subtitle ?? themeColors.subtitle ?? DEFAULT_COLORS.subtitle,
      primary: widgetColors.primary ?? themeColors.primary ?? DEFAULT_COLORS.primary,
      background: widgetColors.background ?? themeColors.background ?? DEFAULT_COLORS.background,
    };
  });

  background = computed(() => this.mergedColors()?.background);
  titleColor = computed(() => this.mergedColors()?.title);
  subtitleColor = computed(() => this.mergedColors()?.subtitle);
  cornerRadius = computed(() => this.themeSig()?.cornerRadius ?? 0);
  titleFont = computed(() => this.themeSig()?.fonts?.title?.name ?? this.themeSig()?.fonts?.plain?.name ?? 'inherit');
  subtitleFont = computed(() => this.themeSig()?.fonts?.subtitle?.name ?? this.themeSig()?.fonts?.plain?.name ?? 'inherit');
  paddingTop = computed(() => this.widget()?.widgetStyle?.['padding']?.top ?? DEFAULT_PADDING.top);
  paddingBottom = computed(() => this.widget()?.widgetStyle?.['padding']?.bottom ?? DEFAULT_PADDING.bottom);
  textAlign = computed(() => {
    const align = this.widget()?.widgetStyle?.['contentAlignment'];
    if (align === 'leading') return 'left';
    if (align === 'center') return 'center';
    if (align === 'trailing') return 'right';
    return 'left'; // default
  });

  // error handling

  onImgError(event: Event) {
    const w = this.widgetSig();
    if (!w) return;

    if (w.fallbackUrl && this.imgSrcSig() !== w.fallbackUrl) {
      this.imgSrcSig.set(w.fallbackUrl);
      return;
    }

    // no better fallback: hide image?? or??
    const img = event.target as HTMLImageElement;
    img.style.display = 'none';
  }
}
